import { PILAR_LABEL } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronRight, Mail, Moon, Plus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj, type ObjId } from '../../components/Obj.tsx'
import { SkCard, SkGarden } from '../../components/Skeleton.tsx'
import { ApiError } from '../../lib/api.ts'
import { sparkBurst } from '../../lib/motion.ts'
import { useToast } from '../../lib/toast.ts'
import { Garden } from './Garden.tsx'
import { HabitsCard } from './Kebiasaan.tsx'
import { useGarden } from './LevelUp.tsx'
import { addNiat, refreshPerjalanan, setNiatDone, useHub, type Garden as GardenState, type Hub, type Niat, type Saran } from './perjalanan.ts'

const msg = (e: unknown) => (e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')

function MuhasabahCard({ done, malam }: { done: boolean; malam: boolean }) {
  return (
    <Link className={`card row-card pj-muh reveal${malam && !done ? ' now' : ''}`} to="/perjalanan/muhasabah">
      <span className="ic" aria-hidden="true">{done ? <Check size={20} /> : <Moon size={20} />}</span>
      <div>
        <b>{done ? 'Muhasabah hari ini sudah' : 'Muhasabah 60 detik'}</b>{' '}
        <small>{done ? 'Terima kasih sudah menengok harimu. Bisa diubah kapan saja hari ini.' : 'Tiga pertanyaan singkat sebelum tidur: langkahmu, syukurmu, perbaikanmu.'}</small>
      </div>
      <ChevronRight className="chev" size={18} aria-hidden="true" />
    </Link>
  )
}

function TantanganCard({ t }: { t: Hub['tantangan'] }) {
  return (
    <section className="card pj-sec reveal" aria-labelledby="pj-tt-h">
      <div className="pj-sec-h">
        <h2 id="pj-tt-h">Tantangan 7 hari</h2>
        <Link className="pj-more" to="/perjalanan/tantangan">Semua</Link>
      </div>
      {t.aktif.length === 0 ? (
        <Link className="row-card pj-inner" to="/perjalanan/tantangan">
          <span className="ic obj-ic" aria-hidden="true"><Obj id="pijakan" /></span>
          <div><b>Pilih satu tantangan</b>{' '}<small>{t.tersedia > 0 ? `${t.tersedia} pilihan: satu ayat dan satu langkah kecil tiap hari.` : 'Tantangan sedang disiapkan.'}</small></div>
          <ChevronRight className="chev" size={18} aria-hidden="true" />
        </Link>
      ) : (
        <div className="stack">
          {t.aktif.map((p) => {
            const run = p.run!
            return (
              <Link key={p.id} className="row-card pj-inner" to={run.openToday ? `/perjalanan/tantangan/${p.id}/${run.next}` : `/perjalanan/tantangan/${p.id}`}>
                <span className="ic obj-ic" aria-hidden="true"><Obj id={p.obj as ObjId} /></span>
                <div>
                  <b>{p.judul}</b>{' '}
                  <small>{run.openToday ? `Hari ke-${run.next} sudah terbuka` : `Hari ke-${run.next} terbuka besok`} · {run.done.length}/7 selesai</small>
                  <span className="pj-days" aria-hidden="true">{Array.from({ length: 7 }, (_, i) => <i key={i} className={run.done.includes(i + 1) ? 'on' : i + 1 === run.next ? 'next' : ''} />)}</span>
                </div>
                <ChevronRight className="chev" size={18} aria-hidden="true" />
              </Link>
            )
          })}
        </div>
      )}
    </section>
  )
}

function NiatCard({ niat, onLight, onError }: { niat: Hub['niat']; onLight: (g: GardenState) => void; onError: (m: string) => void }) {
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [pending, setPending] = useState(false)
  const add = async (e: FormEvent) => {
    e.preventDefault()
    if (!text.trim()) return
    setPending(true)
    try {
      await addNiat(text.trim())
      setText('')
      await refreshPerjalanan(qc)
    } catch (err) {
      onError(msg(err))
    } finally {
      setPending(false)
    }
  }
  const done = async (n: Niat, el: HTMLElement) => {
    sparkBurst(el, 12)
    try {
      onLight((await setNiatDone(n.id, true)).garden)
    } catch (err) {
      onError(msg(err))
    }
  }
  return (
    <section className="card pj-sec reveal" aria-labelledby="pj-niat-h">
      <div className="pj-sec-h">
        <h2 id="pj-niat-h">Niat &amp; Amal</h2>
        <Link className="pj-more" to="/perjalanan/niat">{niat.openCount > 3 ? `Semua (${niat.openCount})` : 'Semua'}</Link>
      </div>
      {niat.open.length > 0 ? (
        <ul className="nt-list">
          {niat.open.map((n) => (
            <li key={n.id}>
              <button type="button" role="checkbox" aria-checked={false} className="hb-check" onClick={(e) => void done(n, e.currentTarget)}>
                <span className="hb-box" aria-hidden="true" />
                <span className="hb-label">{n.body}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted pj-empty">Tulis satu niat kecil, lalu centang saat sudah kamu kerjakan.</p>
      )}
      <form className="nt-add" onSubmit={add}>
        <label className="sr" htmlFor="pj-niat-in">Niat baru</label>
        <input id="pj-niat-in" className="input" value={text} maxLength={160} onChange={(e) => setText(e.target.value)} placeholder="Misalnya: menelepon ibu sore ini" />
        <button type="submit" className="icon-btn" aria-label="Tambah niat" disabled={pending || !text.trim()}><Plus size={20} aria-hidden="true" /></button>
      </form>
    </section>
  )
}

export const saranText = (s: Saran) =>
  s.kind === 'kebiasaan' ? <>Coba tambahkan kebiasaan “{s.label}”.</>
  : s.kind === 'tantangan' ? <>Coba tantangan “{s.judul}”.</>
  : <>Lanjutkan langkah kecilmu yang sudah berjalan.</>

function KompasMini({ k }: { k: Hub['kompas'] }) {
  return (
    <Link className="card pj-sec pj-kompas reveal" to="/perjalanan/kompas">
      <div className="pj-sec-h">
        <h2>Kompas Diri</h2>
        <ChevronRight className="chev" size={18} aria-hidden="true" />
      </div>
      <ul className="kp-bars">
        {k.pilar.map((p) => (
          <li key={p.pilar}>
            <span className="kp-l">{p.label}</span>
            <span className="kp-bar" aria-hidden="true"><i style={{ width: `${Math.max(3, p.score)}%` }} /></span>
            <span className="kp-t">{p.tahap}</span>
          </li>
        ))}
      </ul>
      <p className="muted kp-saran">Paling sepi 30 hari ini: {PILAR_LABEL[k.lemah]}. {saranText(k.saran)}</p>
    </Link>
  )
}

/** /perjalanan (docs/UX-PLAYBOOK.md §7.7): garden → habits → muhasabah (first in the evening) → challenges → niat → compass. */
export function PerjalananPage() {
  const { data, isPending, isError, refetch } = useHub()
  const [toast, show] = useToast()
  const { after, overlay } = useGarden()

  return (
    <div className="page pj">
      <h1>Perjalanan</h1>
      {isPending && (
        <div className="stack">
          <SkGarden label="Memuat tamanmu" />
          <SkCard lines={4} />
        </div>
      )}
      {isError && (
        <EmptyState obj="pijakan" title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && (
        <>
          {data.jumat && (
            <Link className="card row-card pj-jumat reveal" to="/perjalanan/jumat">
              <span className="ic amber" aria-hidden="true"><Mail size={20} /></span>
              <div><b>Kabar Jumat sudah datang</b>{' '}<small>Tengok sepekan perjalananmu, dan bekal untuk pekan depan.</small></div>
              <ChevronRight className="chev" size={18} aria-hidden="true" />
            </Link>
          )}
          <Garden light={data.garden.light} level={data.garden.level} />
          {data.malam && <MuhasabahCard done={data.muhasabah.done} malam />}
          <HabitsCard habits={data.kebiasaan.habits} days={data.kebiasaan.days} missedYesterday={data.kebiasaan.missedYesterday} onLight={after} onError={show} />
          {!data.malam && <MuhasabahCard done={data.muhasabah.done} malam={false} />}
          <TantanganCard t={data.tantangan} />
          <NiatCard niat={data.niat} onLight={after} onError={show} />
          <KompasMini k={data.kompas} />
          <nav className="stack pj-links" aria-label="Lainnya di Perjalanan">
            <Link className="card row-card reveal" to="/perjalanan/rencana">
              <span className="ic obj-ic" aria-hidden="true"><Obj id="tunas" /></span>
              <div><b>Rencana tumbuh</b>{' '}<small>{data.rencana ? 'Lihat atau ubah jawabanmu, dan saran yang cocok.' : 'Tiga pertanyaan singkat untuk saran yang pas.'}</small></div>
              <ChevronRight className="chev" size={18} aria-hidden="true" />
            </Link>
            {!data.jumat && (
              <Link className="card row-card reveal" to="/perjalanan/jumat">
                <span className="ic obj-ic" aria-hidden="true"><Obj id="kado" /></span>
                <div><b>Kabar Jumat</b>{' '}<small>Rangkuman sepekan, datang tiap Jumat.</small></div>
                <ChevronRight className="chev" size={18} aria-hidden="true" />
              </Link>
            )}
          </nav>
        </>
      )}
      <div className="toast-slot" role="status">{toast && <p className="toast">{toast}</p>}</div>
      {overlay}
    </div>
  )
}
